export type CompositionAgentOp =
    | 'ping'
    | 'seek'
    | 'rasterize'
    | 'serialize'
    | 'timelineInfo'
    | 'setNodeText'
    | 'setNodeStyle';

export interface CompositionAgentRequest {
    l5: 1;
    token: string;
    id: number;
    op: CompositionAgentOp;
    args?: Record<string, unknown>;
}

export interface CompositionAgentResponse {
    l5: 1;
    token: string;
    id: number;
    ok: boolean;
    result?: unknown;
    error?: string;
}

export interface CompositionSerialization {
    html: string;
    /** Elements in the serialized copy, for the fidelity check. Counted on what `html` was built from. */
    elementCount: number;
    /** Elements carrying a shadow root, whose content no serialization of the copy can preserve. */
    shadowHosts: number;
}

function createCompositionAgent(token: string): void {
    const listen = window.addEventListener.bind(window);
    const stringify = JSON.stringify;
    let port: MessagePort | null = null;

    interface SeekableTimeline {
        progress(value: number, suppressEvents?: boolean): unknown;
        totalTime?(value: number, suppressEvents?: boolean): unknown;
    }

    function timeline(): SeekableTimeline | null {
        const registry = (window as unknown as { __timelines?: { main?: SeekableTimeline } })
            .__timelines;
        return registry?.main ?? null;
    }

    function seek(args: Record<string, unknown>): null {
        const target = timeline();
        if (!target) {
            return null;
        }
        const progress = Number(args.progress) || 0;
        const clamped = Math.max(0, Math.min(1, progress));
        try {
            if (args.mode === 'totalTime' && typeof target.totalTime === 'function') {
                target.totalTime(Number(args.seconds) || 0, false);
            } else {
                target.progress(clamped, false);
            }
        } catch {}
        return null;
    }

    async function rasterize(args: Record<string, unknown>): Promise<ImageBitmap> {
        const snap = (
            window as unknown as {
                snapdom?: { toCanvas(node: Element, options: unknown): Promise<HTMLCanvasElement> };
            }
        ).snapdom;
        if (!snap) {
            throw new Error('snapdom is not available in the composition frame');
        }
        const canvas = await snap.toCanvas(document.documentElement, {
            backgroundColor: 'transparent',
            clip: 'viewport',
            width: Number(args.width) || 0,
            height: Number(args.height) || 0,
            dpr: Number(args.scale) || 1,
        });
        return createImageBitmap(canvas);
    }

    function serialize(): CompositionSerialization {
        // Scripts are dropped from the copy rather than carried into it: the inert frame cannot run
        // them, and the inlined snapDOM source alone is half the serialized payload — re-sent over
        // the port and re-parsed at every pose a sampling rule takes.
        const clone = document.documentElement.cloneNode(true) as HTMLElement;
        const scripts = clone.querySelectorAll('script');
        for (let i = 0; i < scripts.length; i++) {
            scripts[i].remove();
        }
        return {
            // Counted on the clone, which is what the copy is parsed from, so the fidelity check
            // compares like with like instead of tripping over the scripts removed here. Descendants
            // of the root element, not of the document — the check counts the copy the same way.
            html: `<!doctype html>${clone.outerHTML}`,
            elementCount: clone.getElementsByTagName('*').length,
            shadowHosts: shadowHostCount(),
        };
    }

    // Shadow content survives neither `outerHTML` nor `getElementsByTagName`, so it cannot be caught
    // by comparing element counts — the host is counted identically on both sides. Report it
    // directly instead, so the copy is rejected rather than silently measured without it.
    function shadowHostCount(): number {
        let hosts = 0;
        const all = document.getElementsByTagName('*');
        for (let i = 0; i < all.length; i++) {
            if ((all[i] as HTMLElement).shadowRoot) {
                hosts += 1;
            }
        }
        return hosts;
    }

    function timelineInfo(): { time: number; duration: number; measurable: boolean } | null {
        const target = timeline() as
            | (SeekableTimeline & { time?(): number; duration?(): number })
            | null;
        if (!target) {
            return null;
        }
        if (typeof target.time !== 'function') {
            return { time: 0, duration: 0, measurable: false };
        }
        try {
            return {
                time: target.time(),
                duration: typeof target.duration === 'function' ? target.duration() : 0,
                measurable: true,
            };
        } catch {
            return { time: 0, duration: 0, measurable: false };
        }
    }

    function setNodeText(args: Record<string, unknown>): null {
        const node = document.getElementById(String(args.id));
        if (node) {
            node.innerHTML = String(args.html);
        }
        return null;
    }

    function setNodeStyle(args: Record<string, unknown>): null {
        const node = document.getElementById(String(args.id));
        const styles = (args.styles ?? {}) as Record<string, string>;
        if (node) {
            for (const property of Object.keys(styles)) {
                node.style.setProperty(property, styles[property]);
            }
        }
        return null;
    }

    async function run(op: string, args: Record<string, unknown>): Promise<unknown> {
        if (op === 'ping') return true;
        if (op === 'seek') return seek(args);
        if (op === 'rasterize') return rasterize(args);
        if (op === 'serialize') return serialize();
        if (op === 'timelineInfo') return timelineInfo();
        if (op === 'setNodeText') return setNodeText(args);
        if (op === 'setNodeStyle') return setNodeStyle(args);
        throw new Error(`unknown op ${stringify(op)}`);
    }

    function handle(data: { id?: number; op?: string; args?: Record<string, unknown> }): void {
        if (!port || typeof data.id !== 'number') {
            return;
        }
        const channel = port;
        const id = data.id;
        const answer = async (): Promise<void> => {
            try {
                const result = await run(String(data.op), data.args ?? {});
                const transfer = result instanceof ImageBitmap ? [result] : [];
                channel.postMessage({ id, ok: true, result }, transfer);
            } catch (error: unknown) {
                channel.postMessage({
                    id,
                    ok: false,
                    error: String((error as Error)?.message ?? error),
                });
            }
        };
        void answer();
    }

    listen('message', (event: MessageEvent) => {
        const data = event.data as { l5?: number; token?: string; kind?: string } | null;
        if (port || !data || data.l5 !== 1 || data.token !== token || data.kind !== 'port') {
            return;
        }
        const granted = event.ports && event.ports[0];
        if (!granted) {
            return;
        }
        port = granted;
        granted.onmessage = (message: MessageEvent) => handle(message.data ?? {});
        granted.start();
        if (document.readyState === 'complete') {
            granted.postMessage({ event: 'ready' });
        } else {
            listen('load', () => granted.postMessage({ event: 'ready' }));
        }
    });
}

export function compositionAgentSource(token: string): string {
    return `(${createCompositionAgent.toString()})(${JSON.stringify(token)});`;
}
